import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getMyVault } from '../lib/vault/vaultApi';
import {
  approveSeniorLink,
  listSeniorRecordings,
  type SeniorRecordingsView,
} from '../lib/senior/seniorApi';
import './guardians.css';

function formatDate(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * The account holder's side of the senior "Dorothy" recording flow: approve the
 * one-tap recording links a family member or advisor generated (they are inert
 * until approved), and see the recordings a senior has submitted, waiting for
 * review. A submitted recording is a draft only you can open — reviewing and
 * sealing it to a recipient is done from the message itself.
 */
export default function SeniorRecordingsScreen() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [vaultId, setVaultId] = useState<string | null>(null);
  const [view, setView] = useState<SeniorRecordingsView | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const dash = await getMyVault(api);
      setVaultId(dash.vault.id);
      setView(await listSeniorRecordings(api, dash.vault.id));
    } catch {
      setError('We could not load your recording links just now.');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleApprove(tokenId: string): Promise<void> {
    if (vaultId === null) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await approveSeniorLink(api, vaultId, tokenId);
      setNotice('Approved. The link will now let them record.');
      await load();
    } catch {
      setError('We could not approve that link.');
    } finally {
      setBusy(false);
    }
  }

  const links = view?.links.filter((l) => !l.revoked) ?? [];
  const awaiting = view?.awaiting_review ?? [];

  return (
    <section className="guard">
      <p className="guard__kicker">For someone who can’t use the app</p>
      <h1 className="guard__title">Recordings.</h1>
      <p className="guard__sub">
        A family member or your advisor can create a one-tap link for a grandparent or anyone who
        would find the app hard to use. They record a message; it arrives here for you to review
        before it is ever sent. A link does nothing until you approve it.
      </p>

      {error !== null && (
        <p role="alert" className="guard__alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p role="status" className="guard__notice">
          {notice}
        </p>
      )}

      {awaiting.length > 0 && (
        <>
          <p className="guard__sectionLabel">Waiting for your review</p>
          <ul className="guard__list">
            {awaiting.map((r) => (
              <li key={r.message_id} className="guard__card guard__item">
                <h3 className="guard__name">{r.pre_set_label ?? 'A recorded message'}</h3>
                <p className="guard__meta">
                  Recorded {formatDate(r.submitted_at)}
                  {r.duration_seconds !== null ? ` · ${r.duration_seconds}s` : ''}
                </p>
                <p className="guard__body">
                  This is waiting for you. Watch it, then decide who it goes to and when — or discard
                  it. Only you can open it.
                </p>
                <div className="guard__actions">
                  <div className="la-cta-wrap">
                    <button
                      type="button"
                      className="la-btn-primary"
                      onClick={() => navigate(`/recordings/${r.message_id}/review`)}
                    >
                      Review &amp; seal
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="guard__sectionLabel">Recording links</p>
      {view !== null && links.length === 0 && (
        <div className="guard__card guard__empty">
          <h2 className="guard__cardTitle">No recording links yet.</h2>
          <p className="guard__body">
            Ask your advisor to generate a link, or create one from a family member’s device. It will
            appear here for you to approve.
          </p>
        </div>
      )}

      {links.length > 0 && (
        <ul className="guard__list">
          {links.map((l) => (
            <li key={l.token_id} className="guard__card guard__item">
              <div className="guard__itemHead">
                <div>
                  <h3 className="guard__name">{l.pre_set_label ?? 'Recording link'}</h3>
                  <p className="guard__meta">
                    Expires {formatDate(l.expires_at)} · {l.recording_count}/{l.max_recordings} used
                  </p>
                </div>
                <span className={`guard__pill guard__pill--${l.approved ? 'active' : 'invited'}`}>
                  {l.approved ? 'Approved' : 'Needs your approval'}
                </span>
              </div>
              {!l.approved && (
                <>
                  <p className="guard__body guard__waiting">
                    Until you approve it, this link will not let anyone record — a safeguard so
                    nothing enters your vault without your say-so.
                  </p>
                  <div className="guard__actions">
                    <button
                      type="button"
                      className="la-btn-secondary"
                      disabled={busy}
                      onClick={() => void handleApprove(l.token_id)}
                    >
                      Approve this link
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
